import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { navLinks, heroData, routeLinks } from "../data/mock";
import LogoImg from "../assets/android-chrome-512x512.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileRoutes, setShowMobileRoutes] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  /* ================= EFFECT: SCROLL DETECT ================= */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ================= NAVIGATION LOGIC ================= */
  const handleNavClick = (id, path) => {
    setIsMobileMenuOpen(false);
    setShowMobileRoutes(false);

    if (path) return navigate(path);

    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      // Delay to ensure home page is loaded before scrolling
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 150);
    }
  };

  const isActive = (id) => {
    if (id === "routes" && location.pathname.startsWith("/routes")) return true;
    if (location.pathname.includes(id)) return true;
    if (id === "home" && location.pathname === "/" && window.scrollY < 100)
      return true;
    return false;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-xl py-2"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
        {/* LOGO AREA */}
        <Link to="/" className="flex items-center gap-2 md:gap-4 group">
          <div className="w-12 h-12 md:w-16 md:h-16 transition-transform duration-300 group-hover:rotate-6">
            <img
              src={LogoImg}
              alt="Bahubali Cabs"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex flex-col">
            <span
              className={`font-black text-xl md:text-3xl tracking-tighter leading-none ${isScrolled ? "text-slate-900" : "text-white"}`}
            >
              BAHUBALI
            </span>
            <span className="text-[10px] md:text-xs font-bold tracking-[0.3em] text-amber-500 uppercase">
              Cabs Udaipur
            </span>
          </div>
        </Link>

        {/* DESKTOP MENU */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <div key={link.id} className="relative group">
              <button
                onClick={() => handleNavClick(link.id, link.path)}
                className={`flex items-center gap-1 font-bold text-base transition-colors ${
                  isActive(link.id)
                    ? "text-amber-500"
                    : isScrolled
                      ? "text-slate-700 hover:text-amber-600"
                      : "text-white/90 hover:text-amber-400"
                }`}
              >
                {link.label}
                {link.id === "routes" && (
                  <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform" />
                )}
              </button>

              {/* Mega Dropdown for Routes */}
              {link.id === "routes" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-2 w-64 grid gap-1">
                    {routeLinks.map((route) => (
                      <button
                        key={route.slug}
                        onClick={() => navigate(`/routes/${route.slug}`)}
                        className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-amber-50 hover:text-amber-700 rounded-xl transition-colors font-semibold text-sm"
                      >
                        <MapPin className="w-4 h-4 text-amber-500" />
                        {route.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA BUTTON */}
        <div className="hidden lg:block">
          <Button
            onClick={() => window.open(`tel:${heroData.phone}`, "_self")}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-black px-8 py-6 rounded-2xl shadow-lg shadow-amber-500/30 transition-all hover:-translate-y-1"
          >
            <Phone className="w-4 h-4 mr-2 fill-current" />
            Book Your Cab
          </Button>
        </div>

        {/* MOBILE TOGGLE */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`lg:hidden p-2.5 rounded-xl transition-colors ${
            isScrolled
              ? "bg-slate-100 text-slate-900"
              : "bg-white/20 text-white"
          }`}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* MOBILE DRAWER */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100 overflow-hidden shadow-2xl"
          >
            <div className="p-6 space-y-4">
              {navLinks.map((link) => (
                <div key={link.id} className="border-b border-slate-50 pb-2">
                  <button
                    onClick={() =>
                      link.id === "routes"
                        ? setShowMobileRoutes(!showMobileRoutes)
                        : handleNavClick(link.id, link.path)
                    }
                    className="flex items-center justify-between w-full py-2 font-black text-lg text-slate-800 uppercase tracking-tight"
                  >
                    {link.label}
                    {link.id === "routes" && (
                      <ChevronDown
                        className={`w-5 h-5 transition-transform ${showMobileRoutes ? "rotate-180" : ""}`}
                      />
                    )}
                  </button>

                  {link.id === "routes" && showMobileRoutes && (
                    <div className="grid gap-3 mt-2 ml-4">
                      {routeLinks.map((r) => (
                        <button
                          key={r.slug}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            navigate(`/routes/${r.slug}`);
                          }}
                          className="text-left text-slate-500 font-bold py-1 flex items-center gap-2"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          {r.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Button
                onClick={() => window.open(`tel:${heroData.phone}`, "_self")}
                className="w-full bg-slate-900 text-white font-black py-7 rounded-2xl mt-4"
              >
                CALL BAHUBALI CABS
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
